import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {DndContext, DragOverlay} from '@dnd-kit/core';
import * as css from './TimetablePage.css';
import TimetableToolbar from './components/timetableToolbar/TimetableToolbar';
import TimetableGrid from './components/timetableGrid/TimetableGrid';
import LectureCard from './components/lectureCard/LectureCard';
import {getTimetable} from '../../redux/timetable/timetableActions';
import {RootState} from '../../redux/store.type';
import {TTimetableItem} from '../../redux/timetable/timetable.type';
import {useTimetableDays} from './hooks/useTimetableDays';
import {useTimeGroups} from './hooks/useTimeGroups';
import {useDragLecture} from './hooks/useDragLecture';
import useShow from '../../hooks/useShow';
import ManageLecturePopup from './components/manageLecturePopup/ManageLecturePopup';
import {useBreakPopup} from './hooks/useBreakPopup';
import PageWrapper from '../components/pageWrapper/PageWrapper';
import Loader from '../../component/loader/Loader';
import ViewToggle from '../../assets/icons/ViewToggle';
import PlusThin from '../../assets/icons/PlusThin';
import ConferenceDates from '../../assets/icons/ConferenceDates';
import DownloadReport from '../../assets/icons/DownloadReport';
import TimetableHeader from './components/timetableHeader/TimetableHeader';
import {useTimetableSearch} from './hooks/useTimetableSearch';
import AddItemMenu from './components/addItemMenu/AddItemMenu';

function TimetablePage() {
    const {t} = useTranslation('timetable');
    const navigate = useNavigate();
    const eid = useSelector((state: RootState) => state.event.eid);
    const lecturesByDay = useSelector((state: RootState) => state.timetable.lectures);
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const [expanded, setExpanded] = useState(true);

    const days = useTimetableDays();
    const getLecturesXHR = useSelector((state: RootState) => state.timetable.getLecturesXHR);
    const isNoDate = days.currentDayIndex === -1;
    const lectures = isNoDate
        ? days.noDateItems
        : (lecturesByDay[days.currentDay?.timestamp] ?? []);
    const {searchQuery, setSearchQuery, visibleLectures} = useTimetableSearch(lectures);
    const timeGroups = useTimeGroups(visibleLectures, isNoDate ? undefined : days.currentDay, slots);
    const drag = useDragLecture(lecturesByDay);
    const lectureFormShow = useShow();
    const [currentLecture, setCurrentLecture] = useState<TTimetableItem | null>(null);
    const [defaultTsid, setDefaultTsid] = useState<number | null>(null);
    const {handleAddBreak, handleBreakEdit, popup: breakPopup} = useBreakPopup();

    const openCreate = (tsid: number | null = null) => {
        setCurrentLecture(null);
        setDefaultTsid(tsid);
        lectureFormShow.open();
    };
    const openEdit = (lecture: TTimetableItem) => {
        setCurrentLecture(lecture);
        setDefaultTsid(null);
        lectureFormShow.open();
    };
    const closeForm = async (e?: Event | React.SyntheticEvent) => {
        await lectureFormShow.close(e);
        setCurrentLecture(null);
        setDefaultTsid(null);
    };

    useEffect(() => {
        if(eid) getTimetable();
    }, [eid]);

    const actions = {
        toggleView: {label: t('toggleView'), onClick: () => setExpanded(e => !e), icon: <ViewToggle />},
        downloadReport: {label: t('downloadLecturesReport'), onClick: () => {}, icon: <DownloadReport />, disabled: true},
        conferenceDates: {label: t('conferenceDates'), onClick: () => navigate(`/e/${eid}/event`), icon: <ConferenceDates />},
        addItem: {label: t('addItem'), onClick: () => openCreate(), icon: <PlusThin />},
        addLecture: {label: t('addLecture'), onClick: () => openCreate(), icon: <PlusThin />},
        addBreak: {label: t('addBreak'), onClick: handleAddBreak, icon: <PlusThin />},
    };

    const buttons = [
        {key: 0, children: actions.toggleView.icon, onClick: actions.toggleView.onClick, withoutPlus: true, variant: 'subtle'},
        {key: 1, children: actions.downloadReport.icon, text: actions.downloadReport.label, onClick: actions.downloadReport.onClick, disabled: actions.downloadReport.disabled, withoutPlus: true, variant: 'neutral'},
        {key: 2, children: actions.conferenceDates.icon, text: actions.conferenceDates.label, onClick: actions.conferenceDates.onClick, withoutPlus: true, variant: 'neutral'},
        {key: 'divider', Comp: () => <css.toolbarDivider />},
        {key: 3, Comp: () => <AddItemMenu text={t('addItem')} variant="neutral" placement="below" iconStart={<PlusThin />} onAddLecture={() => openCreate()} onAddBreak={handleAddBreak} />},
        {key: 4, children: actions.addLecture.icon, text: actions.addLecture.label, onClick: actions.addLecture.onClick, withoutPlus: true, variant: 'brand'},
    ];
    const dateNav = <TimetableToolbar />;
    const routing = {rightContent: dateNav};
    const mobileActions = [
        {key: 5, onClick: days.onPrevDay, text: t('prevDay'), withoutPlus: true},
        {key: 6, text: days.currentDateLabel, withoutPlus: true, list: {text: days.currentDateLabel, arr: days.conferenceDays.map((day: {key: string; label: string}, i: number) => ({text: day.label, key: String(i), onClick: () => days.onDaySelect(i)}))}},
        {key: 7, onClick: days.onNextDay, text: t('nextDay'), withoutPlus: true},
        {key: 8, onClick: actions.toggleView.onClick, text: actions.toggleView.label, withoutPlus: true},
        {key: 9, onClick: actions.addLecture.onClick, text: actions.addLecture.label, withoutPlus: true},
        {key: 13, onClick: actions.addBreak.onClick, text: actions.addBreak.label, withoutPlus: true},
        {key: 11, onClick: actions.conferenceDates.onClick, text: actions.conferenceDates.label, withoutPlus: true},
        {key: 12, onClick: actions.downloadReport.onClick, text: actions.downloadReport.label, withoutPlus: true},
    ];

    const isLoading = getLecturesXHR === 'LOADING';

    return (
        <>
            {/* @ts-expect-error JS component has no TS types */}
            <PageWrapper operations={{buttons, isNewButton: true}} mobileActions={mobileActions} routing={routing} subMenuProps={{wrapCssStyle: '&.changeCss { justify-content: space-between; }'}}>
                <DndContext sensors={drag.sensors} onDragStart={drag.handleDragStart} onDragEnd={drag.handleDragEnd}>
                    <css.container>
                        <TimetableHeader
                            conferenceDatesAction={actions.conferenceDates}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                        />
                        {isLoading
                            ? <Loader XHR="LOADING" height="100%">{null}</Loader>
                            : (
                                <css.contentArea>
                                    <TimetableGrid
                                        timeGroups={timeGroups}
                                        expanded={expanded}
                                        handlers={{
                                            onAddLecture: (tsid: number | null) => openCreate(tsid),
                                            onAddBreak: handleAddBreak,
                                            onAddItem: () => openCreate(),
                                            onEditLecture: openEdit,
                                            onMenuClick: (item: TTimetableItem) => {
                                                if(item.isBreak) handleBreakEdit(item);
                                            },
                                        }}
                                    />
                                </css.contentArea>
                            )
                        }
                    </css.container>
                    <DragOverlay>
                        {drag.activeLecture && (
                            <css.dragOverlayCard $isActive={!!drag.activeLecture}>
                                <LectureCard lecture={drag.activeLecture} expanded={expanded} isOverlay />
                            </css.dragOverlayCard>
                        )}
                    </DragOverlay>
                </DndContext>
            </PageWrapper>
            <ManageLecturePopup show={{...lectureFormShow, close: closeForm}} lecture={currentLecture} defaultTsid={defaultTsid} />
            {breakPopup}
        </>
    );
}

export default TimetablePage;
